import type { AssetId, ConceptId, FilterGroup, FilterGroups, FilterOperator, LabelId, ViewId } from "@storytell/types";
import type { VariableKind } from "@storytell/types/richText";
import { createStore, produce } from "solid-js/store";
import type { Wire } from "~/wire/definition";

// Re-export FilterOperator for use in other components
export type { FilterOperator } from "@storytell/types";

export const isPositiveOperator = (operator: FilterOperator): operator is "and" | "or" =>
  operator === "and" || operator === "or";
export const isNegativeOperator = (operator: FilterOperator): operator is "nand" | "nor" =>
  operator === "nand" || operator === "nor";

export type UIFilterExpression<T> = {
  filter: (item: T) => boolean;
  kind: VariableKind;
  id: AssetId | ConceptId | LabelId | ViewId;
};
export type UIFilterGroup<T> = {
  // Operator used to combine filters inside this group
  operator: FilterOperator;
  filters: UIFilterExpression<T>[];
};
export type Filters<T> = {
  // Global operator used to combine groups with each other
  operator: FilterOperator;
  groups: UIFilterGroup<T>[];
};

export type FilterBuilder<Item extends { id: string }> = ReturnType<typeof useFilterBuilder<Item>>;

/**
 * The UI and BE filters have a similar structure, with one key difference:
 * The UI considers the first operator extraneous, the BE considers the last operator extraneous.
 *
 * For example: [{id: 1, operator: "and"}, {id: 2, operator: "or"}];
 * This is a simplified version of a filter.
 * The UI will consider the `or` as the operator between the two filters, the BE will consider the `and` as the operator between the two filters.
 *
 * The UI structure makes it easier to both display and build the filtering logic,
 * so it's kept different and only converted when sending to the BE.
 */
export const uiFiltersToServerFilters = <Item>(filters: Filters<Item>): FilterGroups => {
  if (filters.groups.length === 0 || filters.groups[0]?.filters.length === 0) return [];

  const result: FilterGroups = [];
  filters.groups.forEach((group) => {
    const g: FilterGroup = {
      group: [],
      // Server expects operator on each group for chaining; with a global operator we can repeat it
      operator: filters.operator || "and",
    };

    group.filters.forEach((filter) => {
      g.group.push({
        id: filter.id as LabelId,
        kind: filter.kind as "label",
        // Server expects operator on each filter for chaining; repeat the group's operator
        operator: group.operator || "and",
      });
    });

    result.push(g);
  });

  return result;
};

export const serverFiltersToUiFilters = <Item extends { id: string }>(
  wire: Wire,
  filters?: FilterGroups,
): Filters<Item> => {
  if (!filters || filters.length === 0) return { operator: "and", groups: [{ operator: "and", filters: [] }] };

  const globalOperator: FilterOperator = filters[0]?.operator || "and";
  const resultGroups: UIFilterGroup<Item>[] = [];

  filters.forEach((group) => {
    const groupOperator: FilterOperator = group.group[0]?.operator || "and";
    const g: UIFilterGroup<Item> = {
      filters: [],
      operator: groupOperator,
    };

    group.group.forEach((filter) => {
      g.filters.push({
        id: filter.id as LabelId,
        kind: filter.kind as "label",
        filter: (entity: Item) => {
          if (entity.id.startsWith("asset_")) {
            return wire.rels.assetHasLabel(entity.id as AssetId, filter.id as LabelId);
          }
          if (entity.id.startsWith("concept_")) {
            return wire.rels.conceptHasLabel(entity.id as ConceptId, filter.id as LabelId);
          }
          if (entity.id.startsWith("view_")) {
            // TODO: Implement wire.sync.getViewHasLabel when views-labels association is available
            return true;
          }
          if (entity.id.startsWith("label_")) {
            // TODO: Handle label-to-label relationships if needed
            return true;
          }
          return true;
        },
      });
    });

    resultGroups.push(g);
  });

  return { operator: globalOperator, groups: resultGroups };
};

export function useFilterBuilder<Item extends { id: string }>() {
  const [state, setState] = createStore<{ filter: Filters<Item> }>({
    filter: { operator: "and", groups: [] },
  });

  const addFilterGroup = (op: FilterOperator = "and", filters: UIFilterExpression<Item>[] = []) => {
    setState(
      "filter",
      produce((prev) => {
        prev.groups.push({ operator: op, filters });
      }),
    );
    return state.filter.groups[state.filter.groups.length - 1]!;
  };

  const addFilter = (filter: UIFilterExpression<Item>, groupIndex: number) => {
    setState(
      produce((state) => {
        state.filter.groups[groupIndex]?.filters.push(filter);
      }),
    );
    return filter;
  };

  const removeFilter = (filterId: string, groupIndex: number) => {
    setState(
      produce((state) => {
        if (!state.filter.groups[groupIndex]) {
          return;
        }
        state.filter.groups[groupIndex].filters = state.filter.groups[groupIndex].filters.filter(
          (f) => f.id !== filterId,
        );
      }),
    );
  };

  const removeFilterGroup = (groupIndex: number) => {
    setState(
      produce((state) => {
        state.filter.groups.splice(groupIndex, 1);
      }),
    );
  };

  const changeGroupOperator = (op: FilterOperator, groupIndex: number) => {
    setState(
      produce((state) => {
        if (!state.filter.groups[groupIndex]) {
          return;
        }
        state.filter.groups[groupIndex].operator = op;
      }),
    );
  };

  const changeGlobalOperator = (op: FilterOperator) => {
    setState(
      produce((state) => {
        state.filter.operator = op;
      }),
    );
  };

  const buildFilterGroupFunction = (group: UIFilterGroup<Item>) => {
    return (item: Item) => {
      if (group.filters.length === 0) return true;

      // For negative operators, we need to check if ALL filters match first
      if (isNegativeOperator(group.operator)) {
        const allMatch = group.filters.every((filter) => filter.filter(item));
        if (group.operator === "nand") {
          // "exclude if all" - exclude if ALL filters match
          return !allMatch;
        } else if (group.operator === "nor") {
          // "exclude if any" - exclude if ANY filter matches
          return !group.filters.some((filter) => filter.filter(item));
        }
      }

      // For positive operators, use the original logic
      let result = group.filters[0].filter(item);
      group.filters.forEach((filter, i) => {
        if (i === 0) return;
        switch (group.operator) {
          case "and":
            result = result && filter.filter(item);
            break;
          case "or":
            result = result || filter.filter(item);
            break;
        }
      });

      return result;
    };
  };

  const buildFilterFunction = () => {
    return (item: Item) => {
      if (state.filter.groups.length === 0) return true;

      // For negative operators, we need to check if ALL groups match first
      if (isNegativeOperator(state.filter.operator)) {
        const allGroupsMatch = state.filter.groups.every((group) => buildFilterGroupFunction(group)(item));
        if (state.filter.operator === "nand") {
          // "exclude if all" - exclude if ALL groups match
          return !allGroupsMatch;
        } else if (state.filter.operator === "nor") {
          // "exclude if any" - exclude if ANY group matches
          return !state.filter.groups.some((group) => buildFilterGroupFunction(group)(item));
        }
      }

      // For positive operators, use the original logic
      let result = buildFilterGroupFunction(state.filter.groups[0]!)(item);
      state.filter.groups.forEach((group, i) => {
        if (i === 0) return;
        switch (state.filter.operator) {
          case "and":
            result = result && buildFilterGroupFunction(group)(item);
            break;
          case "or":
            result = result || buildFilterGroupFunction(group)(item);
            break;
        }
      });

      return result;
    };
  };

  const resetFilter = () => {
    setState({ filter: { operator: "and", groups: [] } });
  };

  const setFilter = (filter: UIFilterExpression<Item>) => {
    setState({ filter: { operator: "and", groups: [{ operator: "and", filters: [filter] }] } });
  };

  return {
    state,
    addFilterGroup,
    addFilter,
    removeFilter,
    removeFilterGroup,
    changeGroupOperator,
    changeGlobalOperator,
    buildFilterFunction,
    resetFilter,
    setFilter,
    setState,
  };
}
